// ─────────────────────────────────────────────────────────────────────────────
// usePolling — the polling loop as a React hook.
//
// Lifecycle:
//
//   mount → fetch → got data/error
//                      │
//                      ├─ data.status === 'pending'       → schedule in 5s
//                      ├─ data.status is terminal         → stop
//                      ├─ error is CHECKOUT_NOT_FOUND     → stop
//                      └─ error is transient (502/504/nw) → schedule with
//                                                            exponential
//                                                            backoff
//
//   unmount → abort in-flight fetch, clear any pending timer
//
// StrictMode safe: the cleanup function cancels the in-flight request AND
// clears the retry timer. If React remounts (dev double-invoke), the second
// effect starts from a clean slate with no zombie fetch in flight.
//
// The hook also exposes `pollNow()` — called when the user taps "I've
// transferred the money" CTA to request an immediate refresh without
// waiting for the next scheduled tick.
//
// Internal structure note:
//   `scheduleNext` needs to call `poll`, and `poll` needs to call
//   `scheduleNext`. Rather than forward-reference one from the other
//   (which TypeScript dislikes and a refactor could silently break),
//   we stash `poll` in a ref. `scheduleNext` reads it through the ref,
//   breaking the definition-order cycle cleanly.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState, useCallback } from 'react';
import type { PublicCheckoutResponse, CheckoutError } from './types';
import { isTerminalStatus, isTerminalError } from './types';
import { fetchCheckout } from './api';

// Base poll interval while status is pending.
const POLL_INTERVAL_MS = 5_000;

// Exponential backoff schedule for transient errors (BANKRAIL_UNAVAILABLE,
// BANKRAIL_TIMEOUT, NETWORK_ERROR). Capped at 30s so the page eventually
// recovers without requiring the user to refresh.
const BACKOFF_SCHEDULE_MS = [5_000, 10_000, 20_000, 30_000] as const;

// Hard backoff for TOO_MANY_REQUESTS — we should never hit this at 12
// polls/minute vs 300/min cap, but if we do, back off aggressively.
const RATE_LIMIT_BACKOFF_MS = 60_000;

export interface UsePollingResult {
  data: PublicCheckoutResponse | null;
  error: CheckoutError | null;
  isPolling: boolean;
  pollNow: () => void;
}

export function usePolling(token: string): UsePollingResult {
  const [data, setData] = useState<PublicCheckoutResponse | null>(null);
  const [error, setError] = useState<CheckoutError | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(true);

  // Refs survive re-renders without triggering them. Used for mutable loop
  // state that must not cause the effect to re-subscribe.
  const backoffStepRef = useRef<number>(0);
  const timerRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef<boolean>(false);

  // Token read via ref so `poll` doesn't need to be re-created when the
  // (rare) case of a prop token change occurs. In practice token is
  // immutable for the lifetime of the component, but the ref makes that
  // invariant explicit.
  const tokenRef = useRef<string>(token);
  tokenRef.current = token;

  // Ref to the latest `poll` function. Populated below, after `poll` is
  // defined. `scheduleNext` reads through this ref to avoid a forward
  // reference. It is never read before `poll` assigns it, because both
  // are only invoked from inside the useEffect that runs poll() first.
  const pollRef = useRef<(() => Promise<void>) | null>(null);

  const scheduleNext = useCallback((delayMs: number): void => {
    if (!mountedRef.current) return;
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      // Read through the ref — guaranteed populated by the time any timer
      // fires, since poll() must have been invoked at least once to get here.
      const current = pollRef.current;
      if (current) void current();
    }, delayMs);
  }, []);

  const poll = useCallback(async (): Promise<void> => {
    if (!mountedRef.current) return;

    // Cancel any in-flight request before starting a new one. Belt-and-
    // suspenders: pollNow() might fire while the scheduled fetch is still
    // open; we want the newer one to win.
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    const result = await fetchCheckout(tokenRef.current, controller.signal);

    // If the component unmounted during the fetch, discard the result silently.
    // This is the key to StrictMode safety.
    if (!mountedRef.current) return;

    // If this controller was superseded by a newer one (pollNow overlapped),
    // discard the older result.
    if (abortRef.current !== controller) return;

    if (result.ok) {
      setData(result.data);
      setError(null);
      backoffStepRef.current = 0;

      if (isTerminalStatus(result.data.status)) {
        setIsPolling(false);
        return;
      }

      // Still pending — schedule next tick.
      scheduleNext(POLL_INTERVAL_MS);
      return;
    }

    // Error path.
    setError(result.error);

    if (isTerminalError(result.error)) {
      setIsPolling(false);
      return;
    }

    // Transient error: back off and retry.
    if (result.error.code === 'TOO_MANY_REQUESTS') {
      scheduleNext(RATE_LIMIT_BACKOFF_MS);
      return;
    }

    const step = Math.min(
      backoffStepRef.current,
      BACKOFF_SCHEDULE_MS.length - 1
    );
    const delay = BACKOFF_SCHEDULE_MS[step] ?? POLL_INTERVAL_MS;
    backoffStepRef.current = step + 1;
    scheduleNext(delay);
  }, [scheduleNext]);

  // Keep the ref synced to the latest `poll`. React guarantees this runs
  // after render commits, before any scheduled setTimeout can fire.
  pollRef.current = poll;

  // Public: immediate poll (CTA click).
  const pollNow = useCallback((): void => {
    if (!mountedRef.current) return;
    // Cancel any pending scheduled tick — pollNow is authoritative.
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    void poll();
  }, [poll]);

  useEffect(() => {
    mountedRef.current = true;
    void poll();

    return () => {
      mountedRef.current = false;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [poll]);

  return { data, error, isPolling, pollNow };
}
